// The button, with the sizes taken away from the call sites.
//
// # What was measured
//
// Twenty-one buttons rendered at 28px tall with 12.8px text and an 8px radius, against
// four that matched the tokens at 32 / 14 / 10. The cause is not that anybody chose
// wrongly: `size="sm"` was passed at a call site, and then whatever flex container the
// button landed in stretched it. A control whose height depends on its neighbour is a
// control with no size at all.
//
// So the size is a PURPOSE here, not a measurement. A screen says what the button is
// for and this file knows how big that is:
//
//   `primary`   the one thing this screen is for. One per surface.
//   `secondary` the other things a person might do here.
//   `quiet`     a control that must not compete — a cancel, a dismiss.
//   `row`       inside a table row, where 32px would make every row taller.
//   `danger`    destructive, and it says so in red.
//
// `size` is deliberately not in the props. If a screen needs a size this file does not
// name, the answer is a new purpose here rather than a number there — which is what
// stops the twenty-second off-token button.
//
// # `shrink-0`, always
//
// The stretching was half the defect and it is fixed structurally: a button never grows
// to fill a row. Two full-bleed buttons measured 1072px and 1104px wide at 1440px,
// because they sat in a flex column and inherited its width. A button that wide is not
// a button, it is a banner somebody can click.

import { type ComponentProps, cloneElement, createContext, useContext } from "react";
import { Button as Base, buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip";

/**
 * What the button is FOR, which is what decides how it looks and how big it is.
 *
 * There is exactly one `primary` per screen. The rule is not aesthetic: a screen with
 * two filled buttons has told the reader that two things are the most important, which
 * is the same as telling them nothing.
 */
export type Purpose =
  | "primary"
  | "secondary"
  | "quiet"
  | "row"
  | "danger"
  | "icon"
  | "preview"
  | "identity";

const PURPOSE: Record<
  Purpose,
  {
    variant: ComponentProps<typeof Base>["variant"];
    size: ComponentProps<typeof Base>["size"];
    className?: string;
  }
> = {
  primary: { variant: "default", size: "default" },
  secondary: { variant: "outline", size: "default" },
  quiet: { variant: "ghost", size: "default" },
  row: { variant: "outline", size: "sm" },
  danger: { variant: "destructive", size: "default" },
  // A square control carrying a glyph and no words — the rail's collapse, a dismiss.
  // It is a PURPOSE and not an exemption: an icon-only button needs an `aria-label`
  // to have a name at all, which is a rule that belongs beside the size.
  icon: { variant: "ghost", size: "icon" },
  // An in-place disclosure that should read as a light reference rather than compete
  // with the pane's footer. It remains a button: opening a preview changes no address.
  preview: { variant: "link", size: "sm" },
  // A relying-party doorway has one decision and no surrounding toolbar. The ordinary
  // 32px product control reads as a row action there, so this named purpose gives the
  // provider action an input-height target and the full reading measure. Call sites do
  // not own either dimension.
  identity: {
    variant: "default",
    size: "default",
    className:
      "min-h-14 w-full justify-center gap-3 rounded-xl border-transparent bg-surface-action px-6 " +
      "text-[length:var(--type-shell-surface-title-size)] text-on-action shadow-(--shadow-panel) " +
      "hover:bg-surface-action-hover hover:text-on-action",
  },
};

/**
 * A CONTROL SITTING IN THE DARK SHELL BAR, and the reason it is spelled once.
 *
 * shadcn's `ghost` variant hard-codes `hover:text-foreground` and
 * `aria-expanded:text-foreground`. Both are right on a light surface and wrong in the
 * topbar, and `text-on-shell` does not override them: tailwind-merge keys on the
 * modifier, so an unmodified `text-*` and a `hover:text-*` are different declarations and
 * BOTH survive.
 *
 * Measured in the running app on 2026-08-06: hovering the search button painted the glyph
 * `#333333` on `#212C3E` — 1.11:1, which is not a dimmer icon, it is no icon. It was
 * invisible to every gate here, because `tools/check-contrast.mjs` measures TOKEN PAIRS
 * and this pair is assembled by a class merge at runtime. That class of defect is still
 * ungated.
 *
 * # It lives HERE now, and that move is the actual fix
 *
 * It was declared in `app/app.tsx` and reached by the three controls in that file, under a
 * docstring saying it was named once "so a fourth control cannot inherit a copy with a
 * piece missing — which is how the first two got it". The fourth control had already
 * inherited exactly that copy: `ui/account.tsx`'s avatar trigger carried
 * `hover:bg-surface-shell-hover aria-expanded:bg-surface-shell-selected` — the two
 * BACKGROUND halves and none of the three COLOUR halves.
 *
 * It was not carelessness, it was the address. `ui/` may not import from `app/`, so the
 * one control outside `app.tsx` **structurally could not reach the constant** and the only
 * way to dress it was to retype it. A shared value declared inside one of its consumers is
 * reachable by that consumer alone, which makes the second consumer a copy by
 * construction. So it moves to the file that already decides what riksu's controls look
 * like, and both spellings collapse into this one.
 *
 * # Why a CONSTANT and not a `Purpose`, against this file's own grain
 *
 * A purpose would be the idiom, and it cannot carry this. The controls in that bar are not
 * all `Action`s: one is a `SheetTrigger` rendering an `Action`, one is `CreateMenu`, one is
 * an `IconAction`, and one is a bare `Button` inside a `DropdownMenuTrigger`. A purpose
 * selects a variant and a size for THIS component; what these four share is a SURFACE they
 * are sitting on, which is orthogonal to what any of them is for. So it composes as a
 * className onto whatever control the bar holds, and the fifth control spends one
 * reference rather than a new component.
 */
export const ON_SHELL_CONTROL =
  "text-on-shell hover:bg-surface-shell-hover hover:text-on-shell " +
  "aria-expanded:bg-surface-shell-selected aria-expanded:text-on-shell";

/** Under an inert measurement ancestor, icon shapes need no button/tooltip state. */
export const ActionMeasurement = createContext(false);

type ActionProps = Omit<ComponentProps<typeof Base>, "variant" | "size"> & {
  readonly purpose?: Purpose;
  /**
   * THE WRITE IS IN FLIGHT.
   *
   * Disables the button and says so with `aria-busy`, which is the half that was missing
   * everywhere: a disabled button tells somebody looking at the screen that the press
   * landed and tells a screen reader nothing except that a control went away.
   *
   * The LABEL DOES NOT CHANGE. Replacing it with "Saving…" resizes the button under the
   * pointer that is still on it, and swapping it for a spinner throws away the only
   * words on the control. What a person needs here is confirmation that their press was
   * received, and a control that has stopped accepting presses is exactly that.
   */
  readonly busy?: boolean;
};

/**
 * An icon-only button, where the LABEL IS A REQUIRED PROP.
 *
 * A glyph is not a name. Four of these shipped: all four had an accessible name — by
 * `aria-label` on two and a visually-hidden span on the others, three spellings of one
 * rule — and none had a TOOLTIP, so a sighted person meeting an unfamiliar glyph had no
 * way to find out what it did except press it.
 *
 * Both come from one prop here, so they cannot disagree and neither can be forgotten. It
 * is a separate component rather than a `purpose` on `Action` because that is what makes
 * the label REQUIRED: a variant could be selected without it and TypeScript would say
 * nothing.
 */
export function IconAction({
  label,
  children,
  className,
  ...props
}: Omit<ActionProps, "purpose"> & { readonly label: string }) {
  const measuring = useContext(ActionMeasurement);
  if (measuring)
    return (
      <Action purpose="icon" className={className} {...props}>
        {children}
      </Action>
    );
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Action purpose="icon" aria-label={label} className={className} {...props}>
            {children}
          </Action>
        }
      />
      {/* The tooltip REPEATS the accessible name rather than adding to it. A tooltip
          carrying information the name does not is information a screen reader never
          gets, and the standard says the same: a tooltip may not be the only place a
          required fact lives. */}
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

export function Action({
  purpose = "secondary",
  busy,
  disabled,
  className,
  ...props
}: ActionProps) {
  const { variant, size, className: purposeClassName } = PURPOSE[purpose];
  const measuring = useContext(ActionMeasurement);
  if (measuring && purpose === "icon")
    return (
      <span
        aria-hidden="true"
        data-slot="button"
        className={cn(buttonVariants({ variant, size }), "shrink-0", purposeClassName, className)}
        style={typeof props.style === "object" ? props.style : undefined}
      >
        {props.children}
      </span>
    );
  return (
    <Base
      variant={variant}
      size={size}
      aria-busy={busy || undefined}
      // `busy` implies disabled, and an explicit `disabled` still wins: a form may be
      // both incomplete and in flight, and the first is the reason worth keeping.
      disabled={disabled || busy}
      // `w-fit`, and the history here is worth keeping because the obvious fix WAS the
      // bug. This read `w-auto` for a long time, on the reasoning that a stated width
      // stops a parent stretching it. Measured on the live page, a history button in a
      // flex column came out **1256px wide**:
      //
      //   as shipped                     1256px
      //   forcing `flex-shrink: 1`       1256px  — unchanged
      //   forcing `width: fit-content`    129px
      //   forcing `align-self: start`     129px
      //
      // Both original classes address the wrong axis, and the second is the CAUSE.
      // `shrink-0` is `flex-shrink`, which governs the MAIN axis — in a flex column
      // that is the height, and stretching happens on the cross axis. And `width: auto`
      // is the exact condition CSS Flexbox §8.3 names for a flex item to be stretched
      // by `align-self: stretch`, which is the default. So `w-auto` was the
      // precondition for the defect it was written to prevent. `w-fit` is
      // `width: fit-content`, which is not stretchable, and it answers the grid case
      // the old comment claimed too.
      //
      // `shrink-0` stays: in a flex ROW it is the right rule and a different one.
      //
      // NOT ON AN ICON. `size="icon"` sets width AND height, and a width utility
      // outranks the width half of it — measured, every icon button in the product was
      // 18px wide inside a 32px-tall control, so the hover fill was a narrow pill with
      // the glyph pressed against both edges. Square is the entire point of the
      // purpose, and a fixed width cannot be stretched by anything.
      className={cn(purpose !== "icon" && "w-fit", "shrink-0", purposeClassName, className)}
      {...props}
    />
  );
}

/**
 * A LINK that looks like an action, because it GOES somewhere.
 *
 * "Go to cash advances" is not a button: it has an address, it belongs in the
 * middle-click menu, it is something a person can copy, and a screen reader should
 * announce it as a link so somebody can find it in the list of links on the page.
 *
 * It is here rather than at a call site for the reason every purpose is: two screens
 * styling their own would be two answers, and the one on the setup screen would be a
 * different height from the one beside it. What it deliberately does NOT do is go
 * through `Action` — Base UI's button is for buttons, and pointing its `render` at an
 * anchor either warns in the console (`nativeButton` defaults true and the anchor loses
 * its native semantics) or, with `nativeButton={false}`, has it stamp `role="button"`
 * over the top, which is exactly the semantics being asked for and exactly not the ones
 * wanted. `buttonVariants` is shadcn's own answer to this and is the same source the
 * button is built from, so the two cannot drift.
 *
 * The element is the caller's: `render` takes a router `Link`, an `<a>` for an external
 * address, whatever the destination needs. This decides only how it looks.
 *
 * # `data-slot="button"`, and it is not cosmetic
 *
 * `app.css` pins three things on that attribute rather than on a class, precisely so no
 * component has to remember them: WHAT a control transitions (`color, background-color,
 * border-color, scale` — deliberately not `outline` or `box-shadow`, because "the focus
 * ring must appear on the frame the key was pressed"), the ring's COLOUR at full strength,
 * and the 44px target floor on a coarse pointer. `Base`'s own render stamps it; this
 * clones the CALLER's anchor, so until now none of the three reached a single link in the
 * product.
 *
 * Measured on the running app before this line, tabbing to the sign-in control:
 * `transition-property: all` at 150ms, `box-shadow` on the frame of the keypress
 * `oklab(0 0 0 / 0) 0 0 0 0` — no ring at all — settling 150ms later at 3px and **half
 * opacity**, because `--tw-ring-color` had never been re-pointed. And `min-block-size:
 * auto` on a phone. After: the ring is present on the first frame, at
 * `--color-focus-color`.
 */
export function ActionLink({
  purpose = "secondary",
  render,
  className,
  children,
}: {
  readonly purpose?: Exclude<Purpose, "icon">;
  /** The anchor this styles. A `<Link to=…>` or an `<a href=…>`. */
  readonly render: React.ReactElement<{
    className?: string;
    children?: React.ReactNode;
    "data-slot"?: string;
  }>;
  readonly className?: string;
  readonly children: React.ReactNode;
}) {
  const { variant, size, className: purposeClassName } = PURPOSE[purpose];
  return cloneElement(render, {
    "data-slot": "button",
    className: cn(
      buttonVariants({ variant, size }),
      // `no-underline`, because the shell's stylesheet underlines links and a filled
      // control with an underline through its label reads as a mistake. The affordance
      // is the fill and the shape; the underline is for a link in prose.
      "w-fit shrink-0 no-underline",
      purposeClassName,
      className,
    ),
    children,
  });
}
